import { ResponseGroupType, ResponseType } from "@/common/interfaces";
import moment from "moment";
import { Moment } from "moment";

export declare type ResponseDict = {[questionUuid: string]: Response};

export declare type ChoiceResponse = {
	choiceUuid: string;
	text?: string | null;
};

export function convertRemoteResponses(remoteResponses: Array<ResponseType>): ResponseDict {
	const result: ResponseDict = {};

	for (const remoteResponse of remoteResponses) {
		result[remoteResponse.questionUuid] = Response.fromRemote(remoteResponse);
	}

	return result;
}

export class Response {
	questionUuid: string;
	text: string | null;
	value: number | null;
	choices: Array<ChoiceResponse> | null;

	constructor(
		questionUuid: string,
		text: string | null,
		value: number | null,
		choices: Array<ChoiceResponse> | null
	) {
		this.questionUuid = questionUuid;
		this.text = text;
		this.value = value;
		this.choices = choices;
	}

	updateFromRemote(remoteResponse: ResponseType): void {
		console.assert(remoteResponse.questionUuid === this.questionUuid, "Mismatch on update to response");
		this.text = remoteResponse.text;
		this.value = remoteResponse.value;
		this.choices = remoteResponse.choices;
	}

	static fromRemote(remoteResponse: ResponseType): Response {
		return new Response(
			remoteResponse.questionUuid,
			remoteResponse.text,
			remoteResponse.value,
			remoteResponse.choices
		);
	}
}

export class ResponseGroup {
	uuid: string;
	surveyUuid: string;
	publishedSurveyUuid: string;
	createdAt: Moment;
	updatedAt: Moment;
	responses: ResponseDict;

	constructor(
		uuid: string,
		surveyUuid: string,
		publishedSurveyUuid: string,
		responses: ResponseDict,
		createdAt: Moment,
		updatedAt: Moment
	) {
		this.uuid = uuid;
		this.surveyUuid = surveyUuid;
		this.publishedSurveyUuid = publishedSurveyUuid;
		this.responses = {...responses};
		this.createdAt = createdAt;
		this.updatedAt = updatedAt;
	}

	getResponse(questionUuid: string): Response | null {
		if (this.responses[questionUuid] === undefined) {
			return null;
		}

		return this.responses[questionUuid];
	}

	setResponse(questionUuid: string, text: string | null, value: number | null, choices: Array<ChoiceResponse> | null): void {
		if (this.responses[questionUuid] === undefined) {
			this.responses[questionUuid] = new Response(
				questionUuid, text, value, choices
			);
			return;
		}
		this.responses[questionUuid].text = text;
		this.responses[questionUuid].value = value;
		this.responses[questionUuid].choices = (choices)? [...choices] : null;
	}

	updateFromRemote(remoteResponseGroup: ResponseGroupType): void {
		console.assert(
			(
				remoteResponseGroup.uuid === this.uuid
				&& remoteResponseGroup.surveyUuid === this.surveyUuid
			),
			"Attempting to update a responsegroup with an entirely different one."
		);

		this.publishedSurveyUuid = remoteResponseGroup.publishedSurveyUuid;

		// We treat the remote as the source of truth for responses
		this.responses = convertRemoteResponses(remoteResponseGroup.responses);

		this.updatedAt = moment(remoteResponseGroup.updatedAt);
	}

	static fromRemote(remoteResponseGroup: ResponseGroupType): ResponseGroup {
		return new ResponseGroup(
			remoteResponseGroup.uuid,
			remoteResponseGroup.surveyUuid,
			remoteResponseGroup.publishedSurveyUuid,
			convertRemoteResponses(remoteResponseGroup.responses),
			moment(remoteResponseGroup.createdAt),
			moment(remoteResponseGroup.updatedAt)
		);
	}
}
